import { Box, Group, Progress, SimpleGrid, Stack, Text, useMantineTheme } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCompactMoney, formatCurrency, formatSignedCurrency, latest, previous } from '../types';
import type { SafeTOrgKey } from '../types';
import { SafeTCardShell } from './SafeTCardShell';
import { safeTColors } from '../safeTColors';
import { parseQuarterToken, resolveActiveGoal, scoreAnnualCountGoal, targetNumber } from '../../statusUtils';
import type { DashboardActiveGoal } from '../../../../types/dashboard';
import { AppTooltip } from '../../../common/AppTooltip';

type RevenueSeriesPoint = {
  quarter: string;
  revenueNewClients: number;
  revenueAllClients: number;
  qoq?: Record<string, unknown>;
  yoy?: Record<string, unknown>;
};

type Props =
  | { mode: 'combined'; series: RevenueSeriesPoint[]; goals?: DashboardActiveGoal[] }
  | { mode: 'org'; orgKey: SafeTOrgKey; series: RevenueSeriesPoint[]; goals?: DashboardActiveGoal[] };

export function RevenueGeneratedCard(props: Props) {
  const theme = useMantineTheme();
  const series = props.series;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);
  const isOrgAnnualView = props.mode === 'org' && (props.orgKey === 'skyway' || props.orgKey === 'droneport');
  const isAnnualRevenueView = isOrgAnnualView || props.mode === 'combined';

  const existingRevenue =
    cur ? Math.max(0, cur.revenueAllClients - cur.revenueNewClients) : null;
  const prevExistingRevenue = prev ? Math.max(0, prev.revenueAllClients - prev.revenueNewClients) : null;

  // Calculate QoQ for New revenue
  const newRevenueQoQ =
    prev && cur && prev.revenueNewClients !== 0
      ? ((cur.revenueNewClients - prev.revenueNewClients) / prev.revenueNewClients) * 100
      : null;

  // Calculate QoQ for Existing revenue
  const existingRevenueQoQ =
    prevExistingRevenue !== null && prevExistingRevenue !== 0 && existingRevenue !== null
      ? ((existingRevenue - prevExistingRevenue) / prevExistingRevenue) * 100
      : null;

  const curQuarterMeta = parseQuarterToken(cur?.quarter ?? '');
  const curYear = curQuarterMeta?.year ?? null;
  const curQuarterNum = curQuarterMeta?.quarter ?? null;

  const ytdRevenue =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterToken(p.quarter);
            return m?.year === curYear && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.revenueAllClients, 0);

  const prevYearYtd =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterToken(p.quarter);
            return m?.year === curYear - 1 && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.revenueAllClients, 0);

  const yoyAbs = ytdRevenue !== null && prevYearYtd !== null ? ytdRevenue - prevYearYtd : null;
  const yoyPct =
    yoyAbs !== null && prevYearYtd !== null && prevYearYtd !== 0 ? (yoyAbs / prevYearYtd) * 100 : null;
  const yoyArrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');

  const scored = scoreAnnualCountGoal({
    ytd: ytdRevenue ?? 0,
    goals: props.goals,
    field: 'revenueAllClients',
    year: curYear ?? undefined,
    quarterLabel: cur?.quarter ?? '',
  });
  const annualGoal = scored.goal ?? null;
  const perQuarterGoal = annualGoal !== null ? annualGoal / 4 : null;
  const status = ytdRevenue === null ? { label: 'Snapshot' as const, color: 'gray' as const } : scored;

  const progressValue =
    annualGoal !== null && ytdRevenue !== null
      ? Math.max(0, Math.min(100, (ytdRevenue / annualGoal) * 100))
      : 0;

  const trend = series.map((p, idx) => {
    const prevPoint = idx > 0 ? series[idx - 1] : null;
    const prevRevenue = prevPoint?.revenueAllClients ?? null;
    const revenue = p.revenueAllClients;
    const qoqAbs = prevRevenue === null ? null : revenue - prevRevenue;
    const qoqPct = prevRevenue === null || prevRevenue === 0 ? null : (qoqAbs! / prevRevenue) * 100;
    return {
      quarter: p.quarter,
      revenue,
      qoqAbs,
      qoqPct,
    };
  });
  const annualTrend = isAnnualRevenueView
    ? (() => {
        const totals = series.reduce<Record<number, number>>((acc, p) => {
          const meta = parseQuarterToken(p.quarter);
          if (!meta) return acc;
          acc[meta.year] = (acc[meta.year] ?? 0) + p.revenueAllClients;
          return acc;
        }, {});
        const years = Object.keys(totals)
          .map((year) => Number(year))
          .filter((year) => Number.isFinite(year));
        const fallbackYear = curYear ?? new Date().getFullYear();
        const minYear = years.length > 0 ? Math.min(...years) : fallbackYear;
        const maxYear = years.length > 0 ? Math.max(...years) : fallbackYear;
        return Array.from({ length: maxYear - minYear + 1 }, (_, idx) => {
          const year = minYear + idx;
          return { year: String(year), revenue: totals[year] ?? 0 };
        });
      })()
    : null;

  const tooltipStyles = {
    contentStyle: {
      backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
      border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
      borderRadius: 10,
      color: theme.colorScheme === 'dark' ? '#fff' : '#000',
      padding: '10px 12px',
      maxWidth: 320,
      whiteSpace: 'normal' as const,
      wordBreak: 'break-word' as const,
    },
    labelStyle: { color: theme.colorScheme === 'dark' ? '#fff' : '#000' },
    itemStyle: { color: theme.colorScheme === 'dark' ? '#fff' : '#000' },
  } as const;

  const qoqArrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const curQoq =
    prev && cur && prev.revenueAllClients !== 0
      ? ((cur.revenueAllClients - prev.revenueAllClients) / prev.revenueAllClients) * 100
      : null;
  const qoqText =
    curQoq === null ? '—' : qoqArrow(curQoq) ? `${qoqArrow(curQoq)}${Math.abs(curQoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  return (
    <SafeTCardShell
      title="Revenue Generated"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
    >
      <Group position="apart" align="flex-start" spacing="lg" noWrap>
        <Stack spacing="sm" style={{ minWidth: 0, flex: 1 }}>
          <Stack spacing={6}>
            <Group position="apart" spacing="md" noWrap>
              <Stack spacing={2}>
                <Text size="xs" color="dimmed">
                  YTD
                </Text>
                <Text size="xl" weight={800}>
                  {ytdRevenue === null ? '—' : formatCurrency(ytdRevenue)}
                </Text>
                <Text size="xs" color="dimmed">
                  {yoyAbs === null || yoyPct === null
                    ? 'YoY: —'
                    : yoyPct === 0
                      ? 'YoY: 0.00%'
                      : `YoY: ${yoyArrow(yoyAbs)}${Math.abs(yoyPct).toFixed(2)}% (${formatSignedCurrency(yoyAbs)})`}
                </Text>
              </Stack>
              <Stack spacing={2} align="flex-end">
                <Text size="xs" color="dimmed">
                  Annual Goal
                </Text>
                <Text size="xl" weight={800}>
                  {annualGoal === null ? '—' : formatCurrency(annualGoal)}
                </Text>
              </Stack>
            </Group>

            <Progress
              value={annualGoal !== null && ytdRevenue !== null ? progressValue : 0}
              color={status.color}
              radius="xl"
              size="lg"
            />
          </Stack>

          {/* Breakdown row (slightly smaller than main scoreboard) */}
          <SimpleGrid cols={3} spacing="md" breakpoints={[{ maxWidth: 700, cols: 1 }]}>
            <Stack spacing={2}>
              <AppTooltip label="Total revenue generated this quarter across all clients." inline>
                <Text
                  size="xs"
                  color="dimmed"
                  component="span"
                  sx={{
                    cursor: 'help',
                    textDecoration: 'underline dotted',
                    textUnderlineOffset: 2,
                    display: 'inline-flex',
                    width: 'fit-content',
                  }}
                >
                  This Quarter
                </Text>
              </AppTooltip>
              <Text size="lg" weight={700}>
                {cur ? formatCurrency(cur.revenueAllClients) : '—'}
              </Text>
              <Text size="xs" color="dimmed">
                {qoqText}
              </Text>
            </Stack>

            <Stack spacing={2}>
              <AppTooltip label="Revenue from clients acquired this quarter." inline>
                <Text
                  size="xs"
                  color="dimmed"
                  component="span"
                  sx={{
                    cursor: 'help',
                    textDecoration: 'underline dotted',
                    textUnderlineOffset: 2,
                    display: 'inline-flex',
                    width: 'fit-content',
                  }}
                >
                  New
                </Text>
              </AppTooltip>
              <Text size="lg" weight={700}>
                {cur ? formatCurrency(cur.revenueNewClients) : '—'}
              </Text>
              <Text size="xs" color="dimmed">
                {newRevenueQoQ === null
                  ? '— QoQ'
                  : newRevenueQoQ === 0
                    ? '0.00% QoQ'
                    : `${qoqArrow(newRevenueQoQ)}${Math.abs(newRevenueQoQ).toFixed(2)}% QoQ`}
              </Text>
            </Stack>

            <Stack spacing={2}>
              <AppTooltip label="Revenue generated this quarter by clients acquired in prior quarters." inline>
                <Text
                  size="xs"
                  color="dimmed"
                  component="span"
                  sx={{
                    cursor: 'help',
                    textDecoration: 'underline dotted',
                    textUnderlineOffset: 2,
                    display: 'inline-flex',
                    width: 'fit-content',
                  }}
                >
                  Existing
                </Text>
              </AppTooltip>
              <Text size="lg" weight={700}>
                {existingRevenue === null ? '—' : formatCurrency(existingRevenue)}
              </Text>
              <Text size="xs" color="dimmed">
                {existingRevenueQoQ === null
                  ? '— QoQ'
                  : existingRevenueQoQ === 0
                    ? '0.00% QoQ'
                    : `${qoqArrow(existingRevenueQoQ)}${Math.abs(existingRevenueQoQ).toFixed(2)}% QoQ`}
              </Text>
            </Stack>
          </SimpleGrid>

          <Stack spacing={4} style={{ marginTop: '1.5rem' }}>
            <Text size="xs" color="dimmed">
              {isAnnualRevenueView ? 'Annual Revenue' : 'Quarterly Revenue'}
            </Text>
            <Box sx={{ height: 140 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={isAnnualRevenueView ? annualTrend! : trend}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
                  <XAxis
                    dataKey={isAnnualRevenueView ? 'year' : 'quarter'}
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                    minTickGap={12}
                  />
                  <YAxis
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: any) => formatCompactMoney(Number(v))}
                    axisLine={false}
                    tickLine={false}
                    width={52}
                    tickMargin={8}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const p: any = payload[0]?.payload;
                      if (!p) return null;

                      const revenueVal: number = p.revenue;
                      const qoqAbs: number | null = p.qoqAbs ?? null;
                      const qoqPct: number | null = p.qoqPct ?? null;

                      const qoqInline =
                        isAnnualRevenueView || qoqAbs === null || qoqPct === null
                          ? null
                          : `QoQ ${qoqAbs > 0 ? '↑' : qoqAbs < 0 ? '↓' : '→'}${Math.abs(qoqPct).toFixed(2)}%`;

                      const yearNum = Number(label);
                      const yearGoal = Number.isFinite(yearNum)
                        ? targetNumber(resolveActiveGoal(props.goals, { grain: 'annual', year: yearNum }), 'revenueAllClients')
                        : undefined;
                      const goalInline = isAnnualRevenueView
                        ? yearGoal != null
                          ? `Goal ${formatCurrency(yearGoal)}`
                          : null
                        : perQuarterGoal !== null
                          ? `Goal ${formatCurrency(perQuarterGoal)}`
                          : null;

                      return (
                        <div style={tooltipStyles.contentStyle as any}>
                          <div
                            style={{
                              ...(tooltipStyles.labelStyle as any),
                              fontWeight: 700,
                              marginBottom: 6,
                              fontSize: 12,
                              opacity: 0.9,
                            }}
                          >
                            {label}
                          </div>
                          <div
                            style={{
                              ...(tooltipStyles.itemStyle as any),
                              fontWeight: 900,
                              fontSize: 14,
                              lineHeight: 1.2,
                              marginBottom: 4,
                            }}
                          >
                            {formatCurrency(revenueVal)}
                          </div>
                          {goalInline || qoqInline ? (
                            <div style={{ ...(tooltipStyles.itemStyle as any), fontSize: 12, opacity: 0.9 }}>
                              {goalInline && qoqInline ? `${goalInline} · ${qoqInline}` : goalInline ?? qoqInline}
                            </div>
                          ) : null}
                        </div>
                      );
                    }}
                  />
                  {!isAnnualRevenueView && perQuarterGoal !== null ? (
                    <ReferenceLine
                      y={perQuarterGoal}
                      stroke={safeTColors.goal}
                      strokeWidth={2}
                      strokeDasharray="6 6"
                    />
                  ) : null}
                  {isAnnualRevenueView && annualGoal !== null ? (
                    <ReferenceLine
                      y={annualGoal}
                      stroke={safeTColors.goal}
                      strokeWidth={2}
                      strokeDasharray="6 6"
                    />
                  ) : null}
                  <Bar
                    dataKey="revenue"
                    name="Revenue"
                    fill="#228be6"
                  barSize={18}
                    radius={[6, 6, 0, 0]}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Stack>
        </Stack>
      </Group>
    </SafeTCardShell>
  );
}
